import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import type { CostCode, ImpactChoice, LocationMinimal, PunchListItemType, PunchListPriority,
  PunchListStatus, PunchWalkthroughDraft, PunchWalkthroughDraftUpdate, PunchWalkthroughSession,
  Trade, TradePartnerMinimal, UserMinimal } from '../types';

export const walkthroughDraftForReview = (draft: PunchWalkthroughDraft): PunchWalkthroughDraftUpdate => ({
  ...(draft.photo_ids === undefined ? {} : { photo_ids: draft.photo_ids }),
  id: draft.id, included: draft.included, title: draft.title, description: draft.description,
  priority: draft.priority, location: draft.location, trade: draft.trade, item_type: draft.item_type,
  due_date: draft.due_date ?? null, punch_manager: draft.punch_manager ?? null,
  assignee: draft.assignee ?? null, final_approver: draft.final_approver ?? null,
  distribution_list: draft.distribution_list ?? [], responsible_trade_partner: draft.responsible_trade_partner ?? null,
  cost_code: draft.cost_code ?? null, cost_impact: draft.cost_impact ?? 'NA',
  schedule_impact: draft.schedule_impact ?? 'NA', is_private: draft.is_private ?? false,
  status: draft.status ?? 'OPEN', hotspot: draft.hotspot ?? null,
});

const name = (user: UserMinimal) => `${user.first_name} ${user.last_name}`.trim() || user.email;
const NONE = '__none__';
const impacts: Array<{ value: ImpactChoice; label: string }> = [
  { value: 'YES', label: 'Yes' }, { value: 'YES_UNKNOWN', label: 'Yes (Unknown)' },
  { value: 'NO', label: 'No' }, { value: 'TBD', label: 'TBD' }, { value: 'NA', label: 'N/A' },
];
const statuses: Array<{ value: PunchListStatus; label: string }> = [
  { value: 'OPEN', label: 'Open' }, { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
  { value: 'COMPLETED', label: 'Completed' }, { value: 'OVERDUE', label: 'Overdue' }, { value: 'CLOSED', label: 'Closed' },
];

type DraftField = Exclude<keyof PunchWalkthroughDraftUpdate, 'id' | 'included' | 'photo_ids'>;

const populated = (draft: PunchWalkthroughDraftUpdate, field: DraftField, spokenLocation: string) => {
  // Required titles and unmatched spoken locations must remain available for correction.
  if (field === 'title') return true;
  if (field === 'location' && spokenLocation.trim()) return true;
  // The API supplies these defaults even when the user never mentioned the field.
  if (field === 'priority') return draft.priority !== 'MEDIUM';
  if (field === 'status') return !!draft.status && draft.status !== 'OPEN';
  if (field === 'cost_impact' || field === 'schedule_impact') return !!draft[field] && draft[field] !== 'NA';
  const value = draft[field];
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value != null && value !== false;
};

interface Props {
  draft: PunchWalkthroughDraftUpdate;
  spokenLocation: string;
  locations: LocationMinimal[];
  trades: Trade[];
  itemTypes: PunchListItemType[];
  users: UserMinimal[];
  tradePartners: TradePartnerMinimal[];
  costCodes: CostCode[];
  hotspots: NonNullable<PunchWalkthroughSession['hotspot_options']>;
  disabled: boolean;
  onChange: <K extends keyof PunchWalkthroughDraftUpdate>(key: K, value: PunchWalkthroughDraftUpdate[K]) => void;
}

/** Filled fields stay in the compact review; the full form remains one click away. */
export function WalkthroughItemFields({ draft, spokenLocation, locations, trades, itemTypes,
  users, tradePartners, costCodes, hotspots, disabled, onChange }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [visibleWhileEditing, setVisibleWhileEditing] = useState(() => new Set<DraftField>(
    (Object.keys(draft) as Array<keyof PunchWalkthroughDraftUpdate>)
      .filter((field): field is DraftField => field !== 'id' && field !== 'included')
      .filter((field) => populated(draft, field, spokenLocation)),
  ));
  const change = <K extends DraftField>(field: K, value: PunchWalkthroughDraftUpdate[K]) => {
    // Don't unmount a control (or lose focus) when its value is cleared mid-edit.
    setVisibleWhileEditing((fields) => new Set(fields).add(field));
    onChange(field, value);
  };
  const picker = (label: string, value: string, options: Array<{ value: string; label: string }>,
    change: (value: string) => void) => (
    <div key={label}>
      <Label>{label}</Label>
      <Select value={value} onValueChange={change} disabled={disabled}>
        <SelectTrigger className="mt-1.5 w-full min-w-0 [&>span]:truncate" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{options.map((option) => <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  const optional = (options: Array<{ value: string; label: string }>, selected: string | number | null | undefined,
    emptyLabel = 'Not set') => {
    const result = [{ value: NONE, label: emptyLabel }, ...options];
    // Keep a saved reference visible if host option queries are loading or permissions changed.
    if (selected != null && !options.some((option) => option.value === String(selected))) {
      result.push({ value: String(selected), label: `Saved selection (${selected})` });
    }
    return result;
  };
  const recipients = draft.distribution_list ?? [];
  const userPicker = (field: 'punch_manager' | 'assignee' | 'final_approver', label: string) => picker(
    label, draft[field] == null ? NONE : String(draft[field]),
    optional(users.map((user) => ({ value: String(user.id), label: name(user) })), draft[field]),
    (value) => change(field, value === NONE ? null : Number(value)),
  );
  const fields: Record<DraftField, { content: ReactNode; fullWidth?: boolean }> = {
    title: { fullWidth: true, content: <>
      <Label htmlFor={`walkthrough-title-${draft.id}`}>Title</Label>
      <Input id={`walkthrough-title-${draft.id}`} className="mt-1.5" value={draft.title}
        disabled={disabled} onChange={(event) => change('title', event.target.value)} />
    </> },
    description: { fullWidth: true, content: <>
      <Label htmlFor={`walkthrough-description-${draft.id}`}>Description</Label>
      <Textarea id={`walkthrough-description-${draft.id}`} className="mt-1.5 min-h-20"
        value={draft.description} disabled={disabled} onChange={(event) => change('description', event.target.value)} />
    </> },
    priority: { content: picker('Priority', draft.priority,
      [{ value: 'LOW', label: 'Low' }, { value: 'MEDIUM', label: 'Medium' }, { value: 'HIGH', label: 'High' }],
      (value) => change('priority', value as PunchListPriority)) },
    location: { content: picker('Location', draft.location == null ? NONE : String(draft.location),
      optional(locations.map((location) => ({ value: String(location.id),
        label: location.layout_name ? `${location.layout_name} - ${location.name}` : location.name })),
      draft.location, spokenLocation || 'Not set'),
      (value) => change('location', value === NONE ? null : Number(value))) },
    trade: { content: picker('Trade', draft.trade ?? NONE,
      optional(trades.map((trade) => ({ value: trade.id, label: trade.name })), draft.trade),
      (value) => change('trade', value === NONE ? null : value)) },
    item_type: { content: picker('Type', draft.item_type ?? NONE,
      optional(itemTypes.map((type) => ({ value: type.id, label: type.name })), draft.item_type),
      (value) => change('item_type', value === NONE ? null : value)) },
    due_date: { content: <>
      <Label htmlFor={`walkthrough-due-date-${draft.id}`}>Due Date</Label>
      <Input id={`walkthrough-due-date-${draft.id}`} className="mt-1.5" type="date"
        value={draft.due_date?.slice(0, 10) ?? ''} disabled={disabled}
        onChange={(event) => change('due_date', event.target.value ? `${event.target.value}T23:59:00Z` : null)} />
    </> },
    punch_manager: { content: userPicker('punch_manager', 'Punch Manager') },
    assignee: { content: userPicker('assignee', 'Assignee') },
    final_approver: { content: userPicker('final_approver', 'Final Approver') },
    responsible_trade_partner: { content: picker('Responsible Trade Partner', draft.responsible_trade_partner == null ? NONE : String(draft.responsible_trade_partner),
        optional(tradePartners.map((partner) => ({ value: String(partner.id), label: partner.company_name })), draft.responsible_trade_partner),
        (value) => change('responsible_trade_partner', value === NONE ? null : Number(value))) },
    cost_code: { content: picker('Cost Code', draft.cost_code ?? NONE,
        optional(costCodes.map((code) => ({ value: code.id, label: `${code.code} – ${code.name}` })), draft.cost_code),
        (value) => change('cost_code', value === NONE ? null : value)) },
    cost_impact: { content: picker('Cost Impact', draft.cost_impact ?? 'NA', impacts,
      (value) => change('cost_impact', value as ImpactChoice)) },
    schedule_impact: { content: picker('Schedule Impact', draft.schedule_impact ?? 'NA', impacts,
      (value) => change('schedule_impact', value as ImpactChoice)) },
    status: { content: picker('Status', draft.status ?? 'OPEN', statuses, (value) => change('status', value as PunchListStatus)) },
    hotspot: { content: picker('Existing 360 Hotspot', draft.hotspot == null ? NONE : String(draft.hotspot),
        optional(hotspots.map((hotspot) => ({ value: String(hotspot.id),
          label: [hotspot.layout, hotspot.photo, hotspot.name].filter(Boolean).join(' · ') })), draft.hotspot),
        (value) => change('hotspot', value === NONE ? null : Number(value))) },
    distribution_list: { fullWidth: true, content: <fieldset className="space-y-2" disabled={disabled}>
      <legend className="mb-2 text-sm font-medium">Distribution List</legend>
      <div className="max-h-40 space-y-2 overflow-y-auto rounded-xl border border-border bg-muted/30 p-3">
        {users.length === 0 && recipients.length === 0 && <p className="text-xs text-muted-foreground">No recipients selected.</p>}
        {[...users.map((user) => ({ id: user.id, label: name(user) })),
          ...recipients.filter((id) => !users.some((user) => user.id === id)).map((id) => ({ id, label: `Saved user (${id})` })),
        ].map((user) => <label key={user.id} className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox checked={recipients.includes(user.id)} disabled={disabled}
            onCheckedChange={(checked) => change('distribution_list', checked === true
              ? [...recipients, user.id] : recipients.filter((id) => id !== user.id))} />
          {user.label}
        </label>)}
      </div>
    </fieldset> },
    is_private: { fullWidth: true, content: <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
      <Checkbox checked={draft.is_private ?? false} disabled={disabled}
        onCheckedChange={(checked) => change('is_private', checked === true)} />
      Private item
    </label> },
  };
  const keys = Object.keys(fields) as DraftField[];
  const canCollapse = keys.some((field) => !populated(draft, field, spokenLocation));
  return <div className="space-y-2">
    <div id={`walkthrough-fields-${draft.id}`} className="grid gap-3 sm:grid-cols-2">
      {keys.filter((field) => expanded || visibleWhileEditing.has(field) || populated(draft, field, spokenLocation))
        .map((field) => <div key={field} className={`min-w-0 ${fields[field].fullWidth ? 'sm:col-span-2' : ''}`}>
          {fields[field].content}
        </div>)}
    </div>
    {(expanded || canCollapse) && <Button type="button" variant="link" className="h-10 gap-1.5 px-0"
      aria-expanded={expanded} aria-controls={`walkthrough-fields-${draft.id}`}
      onClick={() => {
        if (expanded) setVisibleWhileEditing(new Set(keys.filter((field) => populated(draft, field, spokenLocation))));
        setExpanded(!expanded);
      }}>
      {expanded ? 'See fewer fields' : 'See more fields'}
      {expanded ? <ChevronUp className="size-4" aria-hidden="true" /> : <ChevronDown className="size-4" aria-hidden="true" />}
    </Button>}
  </div>;
}
